import React, { useState, useEffect, useCallback } from 'react';
import { db, storage } from '../../../firebase';
import { collection, getDocs, setDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useDropzone } from 'react-dropzone';
import {
  Container, Typography, Button, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Dialog, DialogActions, DialogContent, DialogTitle, Alert, Chip, Box, Stack, Snackbar, Autocomplete,
  Avatar, Tooltip, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Edit, Delete } from '@mui/icons-material';
import { getFunctions, httpsCallable } from 'firebase/functions';
import MapTeamsModal from './MapTeamsModal';
import { logos } from './logoList';
import { getLogoUrl } from '../../../utils/getLogoUrl';
import { normalizeColor, isValidColor } from '../../../lib/teamColor';
import { findEspnMatch, suggestEspnTeams, findUnmatchedTeams } from '../../../lib/espnTeamMatch';

// Site assets that live in public/logos but aren't team logos.
const NON_TEAM_LOGOS = new Set(['BigBoard_Logo_White.png', 'BlockU_redweb.png']);
const LOGO_OPTIONS = logos.filter((file) => !NON_TEAM_LOGOS.has(file));
const stripExtension = (file) => (file || '').replace(/\.(svg|png|jfif)$/i, '');
// Existing teams store the logo without its extension; match either form.
const logoOptionFor = (value) => {
  if (!value || value.startsWith('http')) return null;
  return LOGO_OPTIONS.find((file) => file === value || stripExtension(file) === stripExtension(value)) || null;
};

const EMPTY_TEAM = {
  name: '',
  conference: '',
  color: '',
  fontColor: '',
  stadium: '',
  logo: '',
  apiTeamName: '',
  shortName: '',
};

// A small logo that degrades to the team's initial when the file is missing
// (or, for a stored value with no extension, when it isn't an .svg).
function TeamLogo({ team, size = 28 }) {
  const [failed, setFailed] = useState(false);
  const src = team.logo ? getLogoUrl(team.logo) : '';
  if (!src || failed) {
    return <Avatar sx={{ width: size, height: size, fontSize: size * 0.5, bgcolor: 'grey.300', color: 'grey.800' }}>{(team.name || '?').charAt(0)}</Avatar>;
  }
  return <Avatar src={src} alt="" variant="rounded" sx={{ width: size, height: size, bgcolor: 'transparent' }} imgProps={{ onError: () => setFailed(true) }} />;
}

function TeamManagement() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [teams, setTeams] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [apiTeams, setApiTeams] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [currentTeam, setCurrentTeam] = useState({ ...EMPTY_TEAM });
  const [logoFile, setLogoFile] = useState(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [isSavingTeam, setIsSavingTeam] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [isDeletingTeam, setIsDeletingTeam] = useState(false);
  // ESPN's team list for the "matches ESPN" check. Loaded once, on demand,
  // through the getEspnTeams function (the browser can't call ESPN directly).
  const [espnTeams, setEspnTeams] = useState(null);
  const [espnStatus, setEspnStatus] = useState('idle'); // idle | loading | ready | error
  const [espnError, setEspnError] = useState('');
  const [isEspnCheckOpen, setIsEspnCheckOpen] = useState(false);
  // The two bulk actions ask first and report through the snackbar.
  const [isMapConfirmOpen, setIsMapConfirmOpen] = useState(false);
  const [isMappingTeams, setIsMappingTeams] = useState(false);
  const [isPrepopulateConfirmOpen, setIsPrepopulateConfirmOpen] = useState(false);
  const [isPrepopulating, setIsPrepopulating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const loadEspnTeams = async () => {
    if (espnStatus === 'loading' || espnStatus === 'ready') return;
    setEspnStatus('loading');
    setEspnError('');
    try {
      const getEspnTeams = httpsCallable(getFunctions(), 'getEspnTeams');
      const result = await getEspnTeams();
      const list = result?.data?.teams;
      if (!Array.isArray(list)) throw new Error('No team list returned');
      setEspnTeams(list);
      setEspnStatus('ready');
    } catch (error) {
      console.error('Error loading ESPN teams:', error);
      setEspnError(error?.message || 'Unknown error');
      setEspnStatus('error');
    }
  };

  const getTeams = useCallback(async () => {
    const data = await getDocs(collection(db, 'teams'));
    setTeams(data.docs.map((d) => ({ ...d.data(), id: d.id })).sort((a, b) => (a.name || '').localeCompare(b.name || '')));
  }, []);

  const onDrop = useCallback((acceptedFiles) => {
    setLogoFile(acceptedFiles[0]);
    const reader = new FileReader();
    reader.onload = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(acceptedFiles[0]);
  }, []);
  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  useEffect(() => {
    getTeams();
  }, [getTeams]);

  useEffect(() => {
    if (currentTeam.logo && !logoFile) {
      if (currentTeam.logo.startsWith('http')) {
        setLogoPreview(currentTeam.logo);
      } else {
        try {
          setLogoPreview(getLogoUrl(currentTeam.logo));
        } catch (err) {
          console.error(err);
        }
      }
    } else if (!currentTeam.logo) {
      setLogoPreview(null);
    }
  }, [currentTeam.logo, logoFile]);

  // Colors are used directly as CSS. A bare hex code gets its missing "#"
  // added on save; anything else the browser can't render blocks the save,
  // since an invalid color silently makes the card's text invisible.
  const colorError = (value) => (
    isValidColor(normalizeColor(value))
      ? ''
      : 'Not a valid color. Use a hex code like #bf5700 or a color name like white.'
  );
  const colorErrors = {
    color: colorError(currentTeam.color),
    fontColor: colorError(currentTeam.fontColor),
  };
  const hasColorError = Boolean(colorErrors.color || colorErrors.fontColor);
  const withSavedColors = (team) => ({
    ...team,
    color: normalizeColor(team.color),
    fontColor: normalizeColor(team.fontColor),
  });

  const formatTeamNameForId = (name) => {
    const words = name.split(' ').slice(0, 2);
    if (words.length === 1) {
      return name.toLowerCase();
    }
    return words[0].toLowerCase() + words.slice(1).map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join('');
  };

  const resolveLogoValue = async () => {
    if (!logoFile) return currentTeam.logo;
    const logoRef = ref(storage, `logos/${logoFile.name}`);
    await uploadBytes(logoRef, logoFile);
    return getDownloadURL(logoRef);
  };

  const handleCreate = async () => {
    if (hasColorError) return;
    setIsSavingTeam(true);
    setSaveError('');
    try {
      const logoValue = await resolveLogoValue();
      const teamId = formatTeamNameForId(currentTeam.name);
      await setDoc(doc(db, 'teams', teamId), { ...withSavedColors(currentTeam), logo: logoValue });
      closeModal();
      getTeams();
      notify(`${currentTeam.name} created.`);
    } catch (error) {
      console.error('Error creating team:', error);
      setSaveError(`Couldn't create the team: ${error.message || 'unknown error'}`);
    } finally {
      setIsSavingTeam(false);
    }
  };

  const handleUpdate = async () => {
    if (hasColorError) return;
    setIsSavingTeam(true);
    setSaveError('');
    try {
      const logoValue = await resolveLogoValue();
      const teamDoc = doc(db, 'teams', currentTeam.id);
      const newFields = { ...withSavedColors(currentTeam), logo: logoValue };
      await updateDoc(teamDoc, newFields);
      closeModal();
      getTeams();
      notify(`${currentTeam.name} updated.`);
    } catch (error) {
      console.error('Error updating team:', error);
      setSaveError(`Couldn't save the team: ${error.message || 'unknown error'}`);
    } finally {
      setIsSavingTeam(false);
    }
  };

  const handleDelete = async () => {
    setIsDeletingTeam(true);
    try {
      const teamDoc = doc(db, 'teams', teamToDelete.id);
      await deleteDoc(teamDoc);
      setTeamToDelete(null);
      setIsDeleteConfirmOpen(false);
      getTeams();
      notify(`${teamToDelete.name} deleted.`);
    } catch (error) {
      console.error('Error deleting team:', error);
      notify(`Couldn't delete the team: ${error.message || 'unknown error'}`, 'error');
    } finally {
      setIsDeletingTeam(false);
    }
  };

  const openDeleteConfirm = (team) => {
    setTeamToDelete(team);
    setIsDeleteConfirmOpen(true);
  };

  const closeDeleteConfirm = () => {
    setTeamToDelete(null);
    setIsDeleteConfirmOpen(false);
  };

  const openModal = (team = null) => {
    if (team) {
      setIsEditing(true);
      setCurrentTeam(team);
    } else {
      setIsEditing(false);
      setCurrentTeam({ ...EMPTY_TEAM });
    }
    setLogoFile(null);
    setLogoPreview(null);
    setSaveError('');
    setIsModalOpen(true);
    loadEspnTeams();
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  // Refreshes the Odds API team list (one API request), then opens the
  // mapping review. Nothing is written to teams until that review is saved.
  const handleMapTeams = async () => {
    setIsMappingTeams(true);
    try {
      const getApiTeamList = httpsCallable(getFunctions(), 'getApiTeamList');
      const result = await getApiTeamList();
      if (!result.data.success) throw new Error(result.data.message || 'Failed to fetch the API team list.');
      const apiTeamsSnapshot = await getDocs(collection(db, 'apiTeams'));
      setApiTeams(apiTeamsSnapshot.docs.map((d) => d.data().name).sort());
      setIsMapConfirmOpen(false);
      setIsMapModalOpen(true);
    } catch (error) {
      console.error('Error fetching API teams list:', error);
      notify(`Couldn't fetch the API team list: ${error.message || 'unknown error'}`, 'error');
    } finally {
      setIsMappingTeams(false);
    }
  };

  // Server-side: fills a blank Short Name with the first word of the team
  // name. Teams that already have one are left alone.
  const handlePrepopulateShortNames = async () => {
    setIsPrepopulating(true);
    try {
      const prepopulateShortNames = httpsCallable(getFunctions(), 'prepopulateShortNames');
      const result = await prepopulateShortNames();
      if (result?.data?.success === false) throw new Error(result.data.message);
      setIsPrepopulateConfirmOpen(false);
      notify('Blank short names filled in.');
    } catch (error) {
      console.error('Error prepopulating short names: ', error);
      notify(`Couldn't fill in short names: ${error.message || 'unknown error'}`, 'error');
      return;
    } finally {
      setIsPrepopulating(false);
    }
    // The server has committed; a failed re-read must not read as a failed fill-in.
    getTeams().catch((error) => notify(`Filled in, but couldn't reload the team list: ${error.message || 'unknown error'}`, 'warning'));
  };

  const teamsMissingShortName = teams.filter((team) => !team.shortName).length;

  // Live "matches ESPN" line for the Add/Edit dialog. A warning, never a
  // block: a team ESPN doesn't carry can still be saved.
  const renderEspnMatch = () => {
    if (!currentTeam.apiTeamName && !currentTeam.shortName) return null;
    if (espnStatus === 'loading' || espnStatus === 'idle') {
      return <Typography variant="caption" color="text.secondary" data-testid="espn-match">Checking ESPN…</Typography>;
    }
    if (espnStatus === 'error') {
      return <Typography variant="caption" color="text.secondary" data-testid="espn-match">Couldn't check ESPN: {espnError}</Typography>;
    }
    const match = findEspnMatch(currentTeam, espnTeams);
    if (match) {
      return (
        <Alert severity="success" sx={{ mt: 1, py: 0 }} data-testid="espn-match">
          Matches ESPN: {match.espnTeam.displayName} (by {match.via === 'apiTeamName' ? 'API Team Name' : 'Short Name'})
        </Alert>
      );
    }
    const suggestions = suggestEspnTeams(currentTeam, espnTeams);
    return (
      <Alert severity="warning" sx={{ mt: 1 }} data-testid="espn-match">
        <Typography variant="body2">
          No ESPN match. ESPN scores, records, kickoff times, and rankings will skip this team.
          It matches when API Team Name equals ESPN's full name, or Short Name equals ESPN's school name.
        </Typography>
        {suggestions.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" display="block">
              Closest ESPN teams. Click one to set Short Name to its school name (Short Name also shows in spread lines):
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
              {suggestions.map((s) => (
                <Chip
                  key={s.id}
                  size="small"
                  label={`${s.displayName} → "${s.location}"`}
                  onClick={() => setCurrentTeam({ ...currentTeam, shortName: s.location })}
                />
              ))}
            </Box>
          </Box>
        )}
      </Alert>
    );
  };

  const openEspnCheck = () => {
    setIsEspnCheckOpen(true);
    loadEspnTeams();
  };
  const unmatchedTeams = espnStatus === 'ready' ? findUnmatchedTeams(teams, espnTeams) : [];

  const field = (label, key, extra = {}) => (
    <TextField
      margin="dense"
      label={label}
      type="text"
      fullWidth
      size="small"
      value={currentTeam[key]}
      onChange={(e) => setCurrentTeam({ ...currentTeam, [key]: e.target.value })}
      {...extra}
    />
  );

  return (
    <Container sx={{ pb: 6 }}>
      <Typography variant="h4" align="center" gutterBottom>
        Team Management
      </Typography>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        useFlexGap
        alignItems={{ xs: 'stretch', md: 'flex-end' }}
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Typography variant="body2" color="text.secondary">
          {teams.length} teams
          {teamsMissingShortName > 0 ? ` · ${teamsMissingShortName} without a short name` : ''}
        </Typography>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent={{ md: 'flex-end' }}>
          <Button variant="contained" color="primary" onClick={() => openModal()}>
            Add New Team
          </Button>
          <Button variant="outlined" onClick={() => setIsMapConfirmOpen(true)}>
            Map Teams
          </Button>
          <Button variant="outlined" onClick={() => setIsPrepopulateConfirmOpen(true)}>
            Pre-populate Short Names
          </Button>
          <Button variant="outlined" onClick={openEspnCheck}>
            Check ESPN Matches
          </Button>
        </Stack>
      </Stack>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Short Name</TableCell>
              <TableCell>Conference</TableCell>
              <TableCell>API Team Name</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {teams.map((team) => (
              <TableRow key={team.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <TeamLogo key={team.logo || 'none'} team={team} />
                    <span>{team.name}</span>
                  </Box>
                </TableCell>
                <TableCell>{team.shortName || <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>}</TableCell>
                <TableCell>{team.conference}</TableCell>
                <TableCell>{team.apiTeamName}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title="Edit team">
                    <IconButton size="small" onClick={() => openModal(team)}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete team">
                    <IconButton size="small" onClick={() => openDeleteConfirm(team)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add / Edit */}
      <Dialog open={isModalOpen} onClose={isSavingTeam ? undefined : closeModal} fullWidth maxWidth="sm" fullScreen={isMobile}>
        <DialogTitle>{isEditing ? 'Edit Team' : 'Add New Team'}</DialogTitle>
        <DialogContent dividers>
          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
          {field('Name', 'name', { autoFocus: true })}
          {field('Conference', 'conference')}
          {field('API Team Name', 'apiTeamName')}
          {field('Short Name', 'shortName')}
          {renderEspnMatch()}
          {field('Color', 'color', { error: Boolean(colorErrors.color), helperText: colorErrors.color || undefined })}
          {field('Font Color', 'fontColor', { error: Boolean(colorErrors.fontColor), helperText: colorErrors.fontColor || undefined })}
          {field('Stadium', 'stadium')}
          <Autocomplete
            options={LOGO_OPTIONS}
            value={logoOptionFor(currentTeam.logo)}
            onChange={(event, option) => {
              // A picked library logo replaces a dropped file; clearing the
              // picker leaves a dropped file in place (it still wins on save).
              if (option) setLogoFile(null);
              setCurrentTeam({ ...currentTeam, logo: option || '' });
            }}
            getOptionLabel={(option) => stripExtension(option)}
            renderOption={(props, option) => {
              const { key, ...rest } = props;
              return (
                <Box component="li" key={key} {...rest} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                  <Avatar src={`/logos/${option}`} alt="" variant="rounded" sx={{ width: 24, height: 24, bgcolor: 'transparent' }} />
                  {stripExtension(option)}
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField {...params} margin="dense" size="small" label="Logo" placeholder="Type to search the logo library" />
            )}
          />
          <Box {...getRootProps()} sx={{ border: '2px dashed', borderColor: 'divider', borderRadius: 1, p: 2, mt: 2, textAlign: 'center', cursor: 'pointer' }}>
            <input {...getInputProps()} />
            <Typography variant="body2" color="text.secondary">
              Or drop a new logo file here, or click to choose one
            </Typography>
          </Box>
          {logoPreview && (
            <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <img src={logoPreview} alt="Logo preview" style={{ width: 64, height: 64, objectFit: 'contain' }} />
              <Typography variant="caption" color="text.secondary">
                {logoFile ? `New file: ${logoFile.name}` : 'Current logo'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal} disabled={isSavingTeam}>Cancel</Button>
          <Button variant="contained" onClick={isEditing ? handleUpdate : handleCreate} loading={isSavingTeam} disabled={hasColorError || !(currentTeam.name || '').trim()}>
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete */}
      <Dialog open={isDeleteConfirmOpen} onClose={() => { if (!isDeletingTeam) closeDeleteConfirm(); }}>
        <DialogTitle>Delete {teamToDelete?.name || 'this team'}?</DialogTitle>
        <DialogContent>
          <Typography>Games that reference this team will show it as missing. This can't be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteConfirm} disabled={isDeletingTeam}>Cancel</Button>
          <Button onClick={handleDelete} color="error" loading={isDeletingTeam}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Map Teams: confirm, then the review modal */}
      <Dialog open={isMapConfirmOpen} onClose={() => { if (!isMappingTeams) setIsMapConfirmOpen(false); }}>
        <DialogTitle>Refresh the API team list?</DialogTitle>
        <DialogContent>
          <Typography>
            This fetches the current team names from the Odds API (one API request against the quota) and then
            opens a review where you match each team. Nothing changes until you save that review.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsMapConfirmOpen(false)} disabled={isMappingTeams}>Cancel</Button>
          <Button variant="contained" onClick={handleMapTeams} loading={isMappingTeams}>Fetch and review</Button>
        </DialogActions>
      </Dialog>

      {/* Pre-populate Short Names */}
      <Dialog open={isPrepopulateConfirmOpen} onClose={() => { if (!isPrepopulating) setIsPrepopulateConfirmOpen(false); }}>
        <DialogTitle>Fill in blank short names?</DialogTitle>
        <DialogContent>
          <Typography>
            {teamsMissingShortName === 0
              ? 'Every team already has a short name, so this will change nothing.'
              : `${teamsMissingShortName} team${teamsMissingShortName === 1 ? '' : 's'} without a short name will get the first word of the team name (for example "Utah" for Utah Utes). Teams that already have one are left alone.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsPrepopulateConfirmOpen(false)} disabled={isPrepopulating}>Cancel</Button>
          <Button variant="contained" onClick={handlePrepopulateShortNames} loading={isPrepopulating} disabled={teamsMissingShortName === 0}>
            Fill in
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isEspnCheckOpen} onClose={() => setIsEspnCheckOpen(false)} fullWidth maxWidth="sm" fullScreen={isMobile}>
        <DialogTitle>ESPN Match Check</DialogTitle>
        <DialogContent>
          {(espnStatus === 'loading' || espnStatus === 'idle') && <Typography>Loading ESPN's team list…</Typography>}
          {espnStatus === 'error' && <Alert severity="error">Couldn't load ESPN's team list: {espnError}</Alert>}
          {espnStatus === 'ready' && unmatchedTeams.length === 0 && (
            <Alert severity="success" data-testid="espn-check-result">All {teams.length} teams match ESPN.</Alert>
          )}
          {espnStatus === 'ready' && unmatchedTeams.length > 0 && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }} data-testid="espn-check-result">
                {unmatchedTeams.length} of {teams.length} teams don't match ESPN. ESPN scores, records, kickoff times, and rankings skip them.
              </Alert>
              {unmatchedTeams.map(({ team, suggestions }) => (
                <Box key={team.id} data-testid="espn-unmatched-team" sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1, borderBottom: '1px solid #eee' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1">{team.name}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      API Team Name: {team.apiTeamName || '(none)'} · Short Name: {team.shortName || '(none)'}
                    </Typography>
                    <Typography variant="caption" display="block">
                      {suggestions.length > 0
                        ? `Closest ESPN team: ${suggestions[0].displayName} (school name "${suggestions[0].location}")`
                        : 'No close ESPN team found.'}
                    </Typography>
                  </Box>
                  <Button size="small" onClick={() => { setIsEspnCheckOpen(false); openModal(team); }}>Edit</Button>
                </Box>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsEspnCheckOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <MapTeamsModal open={isMapModalOpen} onClose={() => setIsMapModalOpen(false)} teams={teams} apiTeams={apiTeams} />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={(event, reason) => { if (reason !== 'clickaway') setSnackbar((s) => ({ ...s, open: false })); }}
      >
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default TeamManagement;
